import React, { ReactNode } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { RootState } from "../store";
import { logout } from "../store/slices/authSlice";
import { useGetBalanceQuery, useGetExchangeRateQuery, useGetProfileQuery } from "../store/api";
import { useExchangeRateSocket } from "../hooks/useExchangeRateSocket";
import { useTheme } from "../hooks/useTheme";
import ThemeToggle from "./ThemeToggle";

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: var(--gradient-bg);
  position: relative;
  transition: background 0.3s ease;
`;

const Sidebar = styled.aside.withConfig({
  shouldForwardProp: (prop) => !["isOpen", "isMobile"].includes(prop),
})<{ isOpen: boolean; isMobile: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 280px;
  background: none;
  color: var(--text-primary);
  overflow: visible;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 200;
  transform: translateX(
    ${(props) => (props.isMobile ? (props.isOpen ? "0" : "-100%") : "0")}
  );
  pointer-events: ${(props) =>
    props.isMobile ? (props.isOpen ? "auto" : "none") : "auto"};

  @media (max-width: 768px) {
    background: var(--bg-primary);
  }
`;

const SidebarContent = styled.div`
  width: 280px;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 2rem 0.5rem 2rem;
  margin-bottom: 0.5rem;
  color: #94a3b8;
`;

const Logo = styled.img`
  height: 28px;
  width: auto;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const Navigation = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 2rem 2rem 2rem;
  flex: 1;
  justify-content: center;
`;

const NavItem = styled(Link).withConfig({
  shouldForwardProp: (prop) => !["isActive"].includes(prop),
})<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  width: 216px;
  padding: 1rem 1.5rem;
  justify-content: flex-start;
  color: ${(props) => (props.isActive ? "var(--nav-active-text)" : "var(--nav-text)")};
  background: ${(props) =>
    props.isActive ? "var(--nav-active-bg)" : "var(--nav-bg)"};
  border-radius: 1rem;
  text-decoration: none;
  gap: 1rem;
  font-weight: ${(props) => (props.isActive ? "700" : "600")};
  font-size: 0.875rem;
  border: 1px solid
    ${(props) =>
      props.isActive ? "var(--nav-active-border)" : "var(--nav-border)"};
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.isActive ? "var(--nav-active-bg)" : "var(--bg-hover)"};
    border-color: var(--border-hover);
  }
`;

const NavLabel = styled.div.withConfig({
  shouldForwardProp: (prop) => !["isActive"].includes(prop),
})<{ isActive: boolean }>`
  overflow: hidden;
  white-space: nowrap;
  flex: 1;
`;

const NavIcon = styled.div.withConfig({
  shouldForwardProp: (prop) => !["isActive"].includes(prop),
})<{ isActive?: boolean }>`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const MainContent = styled.main.withConfig({
  shouldForwardProp: (prop) => !["sidebarOpen", "isMobile"].includes(prop),
})<{ sidebarOpen: boolean; isMobile: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 100vh;
  margin-left: ${(props) =>
    !props.isMobile && props.sidebarOpen ? "280px" : "0"};
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const Header = styled.header`
  background: transparent;
  padding: 0 2rem;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 100;
  width: 100%;

  @media (max-width: 768px) {
    padding: 0 1rem;
    height: 64px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const BalanceSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  background: var(--bg-glass);
  border-radius: 1rem;
  border: 1px solid var(--border-light);
  backdrop-filter: blur(12px);
  transition: background 0.3s ease, border-color 0.3s ease;

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    gap: 0.5rem;
  }
`;

const ExchangeRateSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  background: var(--bg-glass);
  border-radius: 1rem;
  border: 1px solid var(--border-light);
  backdrop-filter: blur(12px);
  transition: background 0.3s ease, border-color 0.3s ease;

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    gap: 0.5rem;
  }
`;

const ExchangeRateDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ExchangeRateAmount = styled.div`
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.2;
`;

const ExchangeRateLabel = styled.div`
  font-size: 0.6875rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
`;

const BalanceDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const BalanceAmount = styled.div`
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.2;
`;

const BalanceLabel = styled.div`
  font-size: 0.6875rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
`;

const WithdrawButton = styled.button`
  background: var(--accent-primary);
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    background: var(--accent-primary-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-active);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.6875rem;
  }
`;

const UserMenu = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: var(--bg-glass);
  border-radius: 1rem;
  border: 1px solid var(--border-light);
  transition: all 0.3s ease;

  &:hover {
    background: var(--bg-card);
    box-shadow: var(--shadow-md);
  }
`;

const UserAvatar = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  background: #b2b2b2;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1rem;
  box-shadow: var(--shadow-active);
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 768px) {
    display: none;
  }
`;

const UserName = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.2;
`;

const UserRole = styled.span`
  font-size: 0.75rem;
  color: var(--text-secondary);
  line-height: 1.2;
`;

const LogoutButton = styled.button`
  background: var(--bg-glass);
  border: 1px solid var(--border-light);
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.75rem;
  border-radius: 0.75rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.875rem;
  font-weight: 600;

  &:hover {
    background: var(--accent-error-light);
    color: #dc2626;
    border-color: var(--accent-error-border);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 0.5rem;
    font-size: 0.75rem;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  background: var(--gradient-bg);
  transition: background 0.3s ease;
`;

interface LayoutProps {
  children: ReactNode;
}

const navItems = [
  {
    path: "/dashboard",
    label: "Дашборд",
    icon: "/icons/dashboard.svg",
    activeIcon: "/icons/dashboard-active.svg",
  },
  {
    path: "/orders",
    label: "Ордера",
    icon: "/icons/orders.svg",
    activeIcon: "/icons/orders-active.svg",
  },
  {
    path: "/balance",
    label: "Баланс",
    icon: "/icons/balance.svg",
    activeIcon: "/icons/balance-active.svg",
  },
  {
    path: "/appeals",
    label: "Апелляции",
    icon: "/icons/appeals.svg",
    activeIcon: "/icons/appeals-active.svg",
  },
  {
    path: "/settings",
    label: "Настройки",
    icon: "/icons/settings.svg",
    activeIcon: "/icons/settings-active.svg",
  },
  {
    path: "/changelog",
    label: "Что нового",
    icon: "/icons/changelog.svg",
    activeIcon: "/icons/changelog-active.svg",
  },
];

function Layout({ children }: LayoutProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();

  const { user } = useSelector((state: RootState) => state.auth);
  const { data: balance } = useGetBalanceQuery();
  const { data: profile } = useGetProfileQuery();
  // Получаем первоначальный курс через REST API
  const { data: initialExchangeRate } = useGetExchangeRateQuery();

  // Используем WebSocket для получения обновлений курса в реальном времени
  const {
    exchangeRate: wsExchangeRate,
    isConnected: wsConnected,
    error: wsError,
  } = useExchangeRateSocket();

  // Используем курс из WebSocket, если доступен, иначе из REST API
  const exchangeRate = wsExchangeRate || initialExchangeRate;

  // Логирование для отладки
  React.useEffect(() => {
    console.log("WebSocket connected:", wsConnected);
    console.log("WebSocket error:", wsError);
    console.log("Exchange rate (WS):", wsExchangeRate);
    console.log("Exchange rate (REST):", initialExchangeRate);
    console.log("Final exchange rate:", exchangeRate);
  }, [wsConnected, wsError, wsExchangeRate, initialExchangeRate, exchangeRate]);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };

    // Инициализация
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const getUserInitials = () => {
    if (!user?.login) return "M";
    return user.login.charAt(0).toUpperCase();
  };

  const formatUSDT = (amount: number): string => {
    return `${amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} USDT`;
  };

  const availableBalance = balance?.balance || 0;

  return (
    <LayoutContainer>
      <Sidebar isOpen={!isMobile} isMobile={isMobile}>
        <SidebarContent>
          <LogoContainer>
            <Logo
              src={isDark ? "/VectorDark.svg" : "/Vector.svg"}
              alt="VangardPay Logo"
              onClick={() => navigate("/dashboard")}
            />
          </LogoContainer>
          <Navigation>
            {navItems.map((item) => (
              <NavItem
                key={item.path}
                to={item.path}
                isActive={location.pathname === item.path}
              >
                <NavIcon isActive={location.pathname === item.path}>
                  <img src={location.pathname === item.path ? item.activeIcon : item.icon} alt={item.label} />
                </NavIcon>
                <NavLabel isActive={location.pathname === item.path}>{item.label}</NavLabel>
              </NavItem>
            ))}
          </Navigation>
        </SidebarContent>
      </Sidebar>

      <MainContent sidebarOpen={!isMobile} isMobile={isMobile}>
        <Header>
          <HeaderLeft>{/* Заголовки страниц убраны */}</HeaderLeft>

          <HeaderRight>
            <ExchangeRateSection>
              <ExchangeRateDisplay>
                <ExchangeRateLabel>Курс USDT/RUB</ExchangeRateLabel>
                <ExchangeRateAmount>
                  {exchangeRate ? `${exchangeRate.rate.toFixed(2)} ₽` : "—"}
                </ExchangeRateAmount>
              </ExchangeRateDisplay>
            </ExchangeRateSection>

            <BalanceSection>
              <BalanceDisplay>
                <BalanceLabel>Доступный баланс</BalanceLabel>
                <BalanceAmount>{formatUSDT(availableBalance)}</BalanceAmount>
              </BalanceDisplay>
              <WithdrawButton onClick={() => navigate("/balance")}>
                Вывести
              </WithdrawButton>
            </BalanceSection>

            <ThemeToggle />

            <UserMenu>
              <UserAvatar>{getUserInitials()}</UserAvatar>
              <UserInfo>
                <UserName>{profile?.login || user?.login || "Мерчант"}</UserName>
                <UserRole>Мерчант</UserRole>
              </UserInfo>
            </UserMenu>

            <LogoutButton onClick={handleLogout}>Выйти</LogoutButton>
          </HeaderRight>
        </Header>

        <ContentArea>{children}</ContentArea>
      </MainContent>
    </LayoutContainer>
  );
}

export default Layout;
